import type { RouteObject } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import { EventDetailsPage } from "@/pages/(home)/(company)/events/event-details";
import { store } from "@/store";
import { companyEventsApi } from "@/services/(company)/api.events";
import { importWithRetry } from "@/utils/lazyWithRetry";

const eventsPage = async () => {
  const { CompanyEventsPage } = await importWithRetry(
    () => import("../../pages/(home)/(company)/events"),
  );
  return { Component: CompanyEventsPage };
};

const eventsListPage = async () => {
  const mod = await importWithRetry(
    () => import("../../pages/(home)/(company)/events/list"),
  );
  return { Component: mod.default };
};

const eventCreationPage = async () => {
  const { CompanyEventCreationForm } = await importWithRetry(
    () => import("../../pages/(home)/(company)/events/_components/event-creation-form"),
  );
  return { Component: CompanyEventCreationForm };
};

export const DYNAMIC_EVENT_ID_KEY = "eventId";
export enum COMPANY_EVENT_PATH_SEGMENTS {
  ROOT = "events",
  CREATE = "create",
  DETAILS = `:${DYNAMIC_EVENT_ID_KEY}`,
  EDIT = "edit",
}

export const companyEventRoutes: RouteObject = {
  path: COMPANY_EVENT_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Events" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout (single "List of Events" tab at the root) ────
    {
      lazy: eventsPage,
      handle: { flushContent: true },
      children: [{ index: true, lazy: eventsListPage }],
    },
    {
      path: COMPANY_EVENT_PATH_SEGMENTS.CREATE,
      handle: { breadcrumb: "Create Event" },
      lazy: eventCreationPage,
    },
    {
      path: COMPANY_EVENT_PATH_SEGMENTS.DETAILS,
      loader: async ({ params }) => {
        const eventId = params[DYNAMIC_EVENT_ID_KEY]!;
        const result = await store.dispatch(companyEventsApi.endpoints.companyGetEventById.initiate({ eventId }));
        const name = result.data?.data?.title ?? `Event #${eventId}`;
        return { breadcrumbLabel: name };
      },
      handle: {
        breadcrumb: (data: { breadcrumbLabel: string }) => data?.breadcrumbLabel ?? "Event Details",
      },
      children: [
        {
          index: true,
          Component: EventDetailsPage,
        },
        {
          path: COMPANY_EVENT_PATH_SEGMENTS.EDIT,
          handle: { breadcrumb: "Edit" },
          lazy: eventCreationPage,
        },
      ],
    },
  ],
};

export const COMPANY_EVENT_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_EVENT_PATH_SEGMENTS.ROOT)),
  CREATE: withCompanyPrefix(addSlashes(COMPANY_EVENT_PATH_SEGMENTS.ROOT, COMPANY_EVENT_PATH_SEGMENTS.CREATE)),
  VIEW: (id: string | number) => withCompanyPrefix(addSlashes(COMPANY_EVENT_PATH_SEGMENTS.ROOT, String(id))),
  EDIT: (id: string | number) =>
    withCompanyPrefix(addSlashes(COMPANY_EVENT_PATH_SEGMENTS.ROOT, String(id), COMPANY_EVENT_PATH_SEGMENTS.EDIT)),
});
